import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { streamUrl } from '../queries/paths';
import { MAX_ATTEMPTS, RESYNC_EVENT, STREAM_STATUS } from '../utils/notice';

// The channel, and only the channel: it owns a connection and reports its
// health. What arrives goes out through the callbacks — this hook holds no
// list, because a list of rows the backend owns is server state and belongs in
// the query cache (TSS vite/03 [RIGID]).
//
// WHY THE NATIVE RECONNECT IS LEFT ALONE. EventSource retries by itself and,
// when it does, it sends the `Last-Event-ID` header with the id of the last
// message it saw. That header is exactly what T15.2 uses to replay the gap.
// Closing the connection to run our own retry loop would throw the header
// away and turn a replayed gap into a lost one.
//
// WHAT WE DO CONTROL is when to stop: the native loop has no cap and no
// backoff, so against a route that is not there it hammers forever. We count
// the failures and close at MAX_ATTEMPTS. From there it is a decision, and
// whoever takes it reloads over HTTP first — see `reconnect` in contexts/live.
//
// THE HEALTH IS KEYED TO THE CONNECTION, not stored loose. `key` identifies
// which connection a reading belongs to, so when the url or the generation
// changes the reading of the connection that just died is not shown as the
// state of the one being opened — it is derived back to `connecting` with no
// effect having to write it.
const OPENING = { status: STREAM_STATUS.connecting, attempts: 0, error: null };

export function useEventStream({ projectId, kinds, subscribe, enabled = true, onNotice, onResync } = {}) {
    const [health, setHealth] = useState({ key: null, ...OPENING });
    const [lastEventId, setLastEventId] = useState(null);
    const [lastNoticeAt, setLastNoticeAt] = useState(null);
    const [generation, setGeneration] = useState(0);

    const onNoticeRef = useRef(onNotice);
    const onResyncRef = useRef(onResync);

    // Callbacks travel in refs, and the refs are written from an effect. Held
    // as effect dependencies instead, the connection would be torn down and
    // reopened on every render of the parent — a reconnect storm dressed up as
    // a re-render.
    useEffect(() => {
        onNoticeRef.current = onNotice;
        onResyncRef.current = onResync;
    }, [onNotice, onResync]);

    // TWO LISTS, AND THEY ARE NOT THE SAME ONE. `subscribe` is what the server
    // is asked for and `kinds` is what this client can actually receive — see
    // STREAM_TYPES in utils/flow for why asking for the exact names fails. Both
    // are strings so the effect does not tear the connection down every time
    // the caller renders a new array with the same contents.
    const listeners = (kinds ?? []).join(',');
    const url = useMemo(
        () => streamUrl({ projectId, types: subscribe }),
        [projectId, subscribe],
    );
    const key = `${url}#${generation}`;

    useEffect(() => {
        if (!enabled) return undefined;

        // Local to this connection, not state: the error handler reads it
        // synchronously and a re-render must not reset the count.
        let failures = 0;
        let abandoned = false;

        const source = new EventSource(url, { withCredentials: true });

        const handleOpen = () => {
            failures = 0;
            setHealth({ key, status: STREAM_STATUS.live, attempts: 0, error: null });
        };

        const handleNotice = (event) => {
            failures = 0;
            if (event.lastEventId) setLastEventId(event.lastEventId);
            setLastNoticeAt(Date.now());

            let payload;
            try {
                payload = JSON.parse(event.data);
            } catch {
                // A notice that does not parse is a contract break, and a
                // silent `return` here would read on screen as a channel that
                // is alive and quiet. It says so instead.
                setHealth(current => ({
                    ...(current.key === key ? current : OPENING),
                    key,
                    error: 'Llegó un aviso que no se pudo leer',
                }));
                return;
            }
            onNoticeRef.current?.(payload);
        };

        const handleResync = () => {
            onResyncRef.current?.();
        };

        const handleError = () => {
            if (abandoned) return;

            // CLOSED means the browser gave up on its own — there is nothing
            // left retrying behind this handler.
            if (source.readyState === EventSource.CLOSED) {
                setHealth({ key, status: STREAM_STATUS.down, attempts: failures, error: null });
                return;
            }

            failures += 1;

            if (failures >= MAX_ATTEMPTS) {
                abandoned = true;
                source.close();
                setHealth({ key, status: STREAM_STATUS.down, attempts: failures, error: null });
                return;
            }
            setHealth({ key, status: STREAM_STATUS.retrying, attempts: failures, error: null });
        };

        // ONE LISTENER PER KIND, because T15.2 sends every event under its own
        // name (`event: task.claimed`) and `EventSource` fires `message` ONLY
        // for events that carry no name. Listening the obvious way — `onmessage`
        // — receives nothing at all: the connection opens, the light goes
        // green, and not one notice ever arrives. `message` stays subscribed
        // anyway, for the day something is published without a name.
        const listenTo = listeners ? listeners.split(',') : [];

        source.addEventListener('open', handleOpen);
        source.addEventListener('message', handleNotice);
        for (const kind of listenTo) source.addEventListener(kind, handleNotice);
        source.addEventListener(RESYNC_EVENT, handleResync);
        source.addEventListener('error', handleError);

        return () => {
            abandoned = true;
            source.removeEventListener('open', handleOpen);
            source.removeEventListener('message', handleNotice);
            for (const kind of listenTo) source.removeEventListener(kind, handleNotice);
            source.removeEventListener(RESYNC_EVENT, handleResync);
            source.removeEventListener('error', handleError);
            source.close();
        };
    }, [url, key, listeners, enabled]);

    // Opens a new connection. Whoever calls it reloads the state over HTTP
    // first: a manual reopen sends no `Last-Event-ID` header, so nothing
    // replays the gap and the only honest way back is to ask again.
    const reconnect = useCallback(() => {
        setGeneration(value => value + 1);
    }, []);

    const current = health.key === key ? health : OPENING;

    return {
        status: enabled ? current.status : STREAM_STATUS.idle,
        attempts: current.attempts,
        error: current.error,
        lastEventId,
        lastNoticeAt,
        reconnect,
    };
}
